import React, { useState, useEffect, useCallback, useMemo } from "react";
import axios from "axios";
import { ChevronLeft, ChevronRight, CalendarDays, CheckCircle2, XCircle } from "lucide-react";
import formatApiError from "../../lib/apiError";

const API = process.env.REACT_APP_BACKEND_URL;
const ax = () => ({ withCredentials: true });

const WEEKDAYS = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];
const MONTHS = [
  "January", "February", "March", "April", "May", "June",
  "July", "August", "September", "October", "November", "December",
];

const REASON_LABEL = {
  content_not_available: "Content Not Available",
  technical_issues:      "Technical Issues",
};

// Build a 6×7 grid of {date: Date, inMonth: bool, key: 'YYYY-MM-DD'} cells.
function buildMonthGrid(year, month) {
  const firstDay = new Date(year, month, 1);
  const startWeekday = firstDay.getDay();              // 0 = Sun
  const start = new Date(year, month, 1 - startWeekday);
  const cells = [];
  for (let i = 0; i < 42; i++) {
    const d = new Date(start);
    d.setDate(start.getDate() + i);
    const yyyy = d.getFullYear();
    const mm   = String(d.getMonth() + 1).padStart(2, "0");
    const dd   = String(d.getDate()).padStart(2, "0");
    cells.push({ date: d, inMonth: d.getMonth() === month, key: `${yyyy}-${mm}-${dd}` });
  }
  return cells;
}

function isSameDate(a, b) {
  return a.getFullYear() === b.getFullYear()
      && a.getMonth()    === b.getMonth()
      && a.getDate()     === b.getDate();
}

export default function CalendarPage() {
  const today = useMemo(() => new Date(), []);
  const [view, setView] = useState({ year: today.getFullYear(), month: today.getMonth() });
  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError]     = useState("");

  const fetchAll = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/api/timetable`, ax());
      setEntries(data);
    } catch (e) {
      setError(formatApiError(e, "Failed to load calendar."));
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  // Group entries by date key
  const entriesByDate = useMemo(() => {
    const map = {};
    for (const e of entries) {
      if (!map[e.date]) map[e.date] = [];
      map[e.date].push(e);
    }
    return map;
  }, [entries]);

  const grid = useMemo(() => buildMonthGrid(view.year, view.month), [view]);

  const goPrev = () => setView((v) => {
    const m = v.month - 1;
    return m < 0 ? { year: v.year - 1, month: 11 } : { year: v.year, month: m };
  });
  const goNext = () => setView((v) => {
    const m = v.month + 1;
    return m > 11 ? { year: v.year + 1, month: 0 } : { year: v.year, month: m };
  });
  const goToday = () => setView({ year: today.getFullYear(), month: today.getMonth() });

  // Stats for the month
  const monthStats = useMemo(() => {
    const inMonth = entries.filter((e) => {
      const d = new Date(e.date);
      return d.getFullYear() === view.year && d.getMonth() === view.month;
    });
    const used = inMonth.filter((e) => e.medimagic_used).length;
    return { total: inMonth.length, used, notUsed: inMonth.length - used };
  }, [entries, view]);

  return (
    <div className="page-wrap">
      <div className="page-header-row">
        <div>
          <h1 className="page-title">Calendar</h1>
          <p className="page-sub">Daily view of timetable entries — green if Medimagic was used, red if not.</p>
        </div>
        <button className="btn-ghost" onClick={goToday} data-testid="cal-today-btn">
          <CalendarDays size={15} /> Today
        </button>
      </div>

      {loading ? <div className="table-loading">Loading…</div>
       : error  ? <div className="table-error">{error}</div>
       : (
        <div className="cal-card">
          {/* Month header */}
          <div className="cal-header">
            <button className="cal-nav-btn" onClick={goPrev} data-testid="cal-prev-btn" aria-label="Previous month">
              <ChevronLeft size={18} />
            </button>
            <h2 className="cal-month-title" data-testid="cal-month-title">
              {MONTHS[view.month]} {view.year}
            </h2>
            <button className="cal-nav-btn" onClick={goNext} data-testid="cal-next-btn" aria-label="Next month">
              <ChevronRight size={18} />
            </button>

            <div className="cal-stats">
              <span className="cal-stat-pill"><strong>{monthStats.total}</strong> Total</span>
              <span className="cal-stat-pill cal-stat-used">
                <CheckCircle2 size={12} /> <strong>{monthStats.used}</strong> Used
              </span>
              <span className="cal-stat-pill cal-stat-not-used">
                <XCircle size={12} /> <strong>{monthStats.notUsed}</strong> Not Used
              </span>
            </div>
          </div>

          {/* Weekday header */}
          <div className="cal-weekdays">
            {WEEKDAYS.map((d) => <div key={d} className="cal-weekday">{d}</div>)}
          </div>

          {/* Grid */}
          <div className="cal-grid">
            {grid.map((cell) => {
              const dayEntries = entriesByDate[cell.key] || [];
              const isToday = isSameDate(cell.date, today);
              return (
                <div
                  key={cell.key}
                  className={`cal-cell${cell.inMonth ? "" : " cal-cell-outside"}${isToday ? " cal-cell-today" : ""}`}
                  data-testid={`cal-cell-${cell.key}`}
                >
                  <div className="cal-cell-head">
                    <span className="cal-date-num">{cell.date.getDate()}</span>
                    {dayEntries.length > 0 && (
                      <span className="cal-count">{dayEntries.length}</span>
                    )}
                  </div>
                  <div className="cal-events">
                    {dayEntries.map((e) => (
                      <div
                        key={e.id}
                        className={`cal-event ${e.medimagic_used ? "cal-event-used" : "cal-event-not-used"}`}
                        title={
                          e.medimagic_used
                            ? `${e.subject_name} — ${e.professor_name}${e.remarks ? `\n${e.remarks}` : ""}`
                            : `${e.subject_name} — ${e.professor_name}\n${REASON_LABEL[e.cancellation_reason] || e.cancellation_reason}${e.cancellation_description ? `: ${e.cancellation_description}` : ""}`
                        }
                        data-testid={`cal-event-${e.id}`}
                      >
                        <span className="cal-event-icon">
                          {e.medimagic_used ? <CheckCircle2 size={11} /> : <XCircle size={11} />}
                        </span>
                        <span className="cal-event-text">
                          {e.subject_code && <span className="cal-event-code">{e.subject_code}</span>}
                          {e.subject_name}
                        </span>
                      </div>
                    ))}
                  </div>
                </div>
              );
            })}
          </div>

          {/* Legend */}
          <div className="cal-legend">
            <span className="cal-legend-item">
              <span className="cal-legend-swatch cal-legend-used" />
              Medimagic Used
            </span>
            <span className="cal-legend-item">
              <span className="cal-legend-swatch cal-legend-not-used" />
              Not Used
            </span>
          </div>
        </div>
      )}
    </div>
  );
}
